import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { GraphQLClient, type Variables } from 'graphql-request';

import { apiToken } from './auth';
import { clusterEndpoint, currentCluster } from './cluster';
import { env } from './env';

/**
 * Which database a read answers from. The Flutter admin sends `1` on every request it
 * makes (`legacy-frontend/packages/core/lib/external/graphql_module.dart:40`,
 * `if (currentPackage == Package.admin)`), and `0` from the tutor screens alone —
 * `schedule_identity_scope.dart:21` spells out what the two mean: *"Admin reads default
 * to the admin replica, where the tutor does not exist; `X-Admin-Database: 0` sends these
 * queries to the live tutoring database."*
 *
 * Sending neither is not a third option: it is `0`. So until now every tool here read
 * live tutoring where the Flutter admin reads the replica, which is a different dataset
 * and not a stale one — dev's Regions answered 25 without the header and 27 with it
 * (measured 2026-10-27, the two the operator had just made being the difference).
 */
const ADMIN_REPLICA = '1';
const LIVE_TUTORING = '0';

/**
 * The graph endpoint on the cluster the region picker names. Read per request rather than
 * once, though a cluster change reloads every tab (`watchCluster`): a request is then never
 * sent to a cluster the cookie no longer names.
 */
export function graphEndpoint(): string {
  return clusterEndpoint(env.VITE_GRAPHQL_ENDPOINT, currentCluster());
}

function clientOn(database: string): GraphQLClient {
  return new GraphQLClient(env.VITE_GRAPHQL_ENDPOINT, {
    requestMiddleware: async (request) => {
      // `request.headers` is a `Headers`, which an object spread empties: copy it, or a
      // per-call header (`x-impersonating-uid`) never leaves the browser.
      const headers = new Headers(request.headers as HeadersInit);
      headers.set('authorization', `Bearer ${await apiToken()}`);
      headers.set('x-admin-database', database);
      return { ...request, url: graphEndpoint(), headers };
    },
  });
}

/**
 * The admin's only backend (`admin-is-an-app` D2): a typed operation on the composed
 * graph, through the Cosmo router, as the signed-in staff member. The edge verifies the
 * token and sends the service `x-user-perms`; the service refuses an operation whose
 * grant the caller lacks, whatever this app shows.
 */
const client = clientOn(ADMIN_REPLICA);

/**
 * The same client against live tutoring, for the surfaces whose Flutter screens read it: the
 * Tutors tool (`src/tools/tutors/graph.ts`) and the account details (`src/lib/accounts/graph.ts`).
 */
const live = clientOn(LIVE_TUTORING);

export function request<Result, Vars extends Variables>(
  document: TypedDocumentNode<Result, Vars>,
  ...variables: Vars extends Record<string, never> ? [] : [Vars]
): Promise<Result> {
  return client.request<Result>({
    document,
    variables: variables[0],
  } as Parameters<typeof client.request<Result>>[0]);
}

/**
 * Run one operation as another account, the way admin.tutero.dev does (`tutors-on-the-graph`
 * D16): the admin's own token, naming the account in `X-Impersonating-Uid`. The edge passes the
 * header on only once tutoring's `canImpersonate` allows the admin, so the self-scoped roots
 * (`tutor`, …) and mutations answer as that account while `@admin` checks still see the admin.
 */
export function requestImpersonating<Result, Vars extends Variables>(
  userId: string,
  document: TypedDocumentNode<Result, Vars>,
  ...variables: Vars extends Record<string, never> ? [] : [Vars]
): Promise<Result> {
  return client.request<Result>({
    document,
    variables: variables[0],
    requestHeaders: { 'x-impersonating-uid': userId },
  } as Parameters<typeof client.request<Result>>[0]);
}

/**
 * The same two, against live tutoring rather than the admin replica — every Flutter tutor
 * screen overrides to it, because the replica is *"where the tutor does not exist"*. Only
 * `src/tools/tutors/graph.ts` and `src/lib/accounts/graph.ts` reach for these; nothing else should.
 */
export function requestLive<Result, Vars extends Variables>(
  document: TypedDocumentNode<Result, Vars>,
  ...variables: Vars extends Record<string, never> ? [] : [Vars]
): Promise<Result> {
  return live.request<Result>({
    document,
    variables: variables[0],
  } as Parameters<typeof live.request<Result>>[0]);
}

/**
 * `requestLive` that a caller can call off: TanStack Query aborts `signal` once nothing waits on
 * the read any more — a search overtaken by the next filter change — so the browser drops it.
 */
export function requestLiveCancellable<Result, Vars extends Variables>(
  signal: AbortSignal | undefined,
  document: TypedDocumentNode<Result, Vars>,
  ...variables: Vars extends Record<string, never> ? [] : [Vars]
): Promise<Result> {
  return live.request<Result>({
    document,
    variables: variables[0],
    signal,
  } as Parameters<typeof live.request<Result>>[0]);
}

export function requestLiveImpersonating<Result, Vars extends Variables>(
  userId: string,
  document: TypedDocumentNode<Result, Vars>,
  ...variables: Vars extends Record<string, never> ? [] : [Vars]
): Promise<Result> {
  return live.request<Result>({
    document,
    variables: variables[0],
    requestHeaders: { 'x-impersonating-uid': userId },
  } as Parameters<typeof live.request<Result>>[0]);
}
